<template>
  <div class="d-flex justify-content-center align-items-center body-wrapper" :class="{ 'dark-theme': isDarkMode }">
    <div class="app-screen" :class="{ 'keyboard-open': isKeyboardOpen }">
      <header class="app-header">
        <img src="@/assets/pic/logoweb.png" alt="Logo" class="login-logo">
      </header>

      <!-- إضافة عنصر جديد للف المحتوى الأبيض -->
      <div class="content-wrapper">
        <main class="main-content" ref="mainContentRef">
          <h1 class="welcome-title fw-bold mb-1">{{ $t('login.title') }}</h1>
          <p class="text-secondary mb-4" style="white-space: nowrap;">
            {{ $t('login.description') }}
          </p>

        
          <!-- Hidden email input - accessible via JS injection from mobile WebView -->
          <input  type="hidden" name="email" id="user-email" />

          <!-- The library will attach to this input field -->
          <input
            v-model="msisdn"
            ref="phoneNumberInput"
            type="tel"
            inputmode="numeric"
            id="phone-number"
            class="mb-4"
            :class="{ 'is-invalid': errors.msisdn || localError }"
            :aria-label="$t('login.phoneInputLabel')"
            :placeholder="$t('login.phoneNumberPlaceholder')"
            :style="{ marginBottom: selectedCountry === 'SA' ? '10px !important' : '24px' }"
          />
          <div class="invalid-feedback" v-if="errors.msisdn || localError">
            {{ errors.msisdn ? errors.msisdn[0] : localError }}
          </div>

          <!-- New STC Service Content -->
          <p v-if="selectedCountry === 'SA'" class="service-price-note text-center mb-4">
            {{ $t('login.stc_price_note') }}
          </p>

          <div v-if="selectedCountry === 'SA'" class="terms-container">
            <h2 class="terms-title">{{ $t('login.terms_title') }}</h2>
            <p class="terms-text">
              {{ $t('login.terms_text') }}
            </p>

            <h2 class="terms-title terms-title--normal mt-3">{{ $t('login.cancel_title') }}</h2>
            <p class="terms-text">
              {{ $t('login.cancel_text') }}
            </p>
          </div>
        </main>

        <footer class="app-footer" ref="footerRef">
          <div class="footer-content-wrapper w-100 d-flex flex-column align-items-center">
            <button 
              type="button" 
              @click="sendOTP" 
              ref="nextButton" 
              id="cta_button" 
              class="btn btn-custom-primary cptpl_subscribe" 
              :disabled="!isPhoneNumberValid || auth.loading">
              <span v-if="auth.loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ auth.loading ? $t('common.loading') || 'Loading...' : $t('login.subscribe') }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/store/auth'; // Keep this import
import { useI18n } from 'vue-i18n'; // Keep this import
import { useRouter, useRoute } from 'vue-router' // Keep this import
import { useAuthFlow } from '@/views/Auth/useAuthFlow';

const iti = ref(null)
const phoneNumberInput = ref(null)
const footerRef = ref(null)
const router = useRouter()
const auth = useAuthStore();
const { resolveFlow, executeFlow } = useAuthFlow();
const route = useRoute();
const msisdn = ref('');
const selectedCountry = ref('SA'); // نحدد السعودية كافتراضي
const deviceType = ref('')
const isDarkMode = ref(route.query.is_dark === 'true');
const isKeyboardOpen = ref(false);
const initialHeight = ref(window.innerHeight);
const localError = ref('');
const serverTid = ref(''); // لتخزين المعرف الرسمي من السيرفر (ليبيا)
const scriptLoaded = ref(false);
let adjustFooter = null;

const fetchProtectedScript = async (suppressLoading = true) => {
  try {
    if (!iti.value) return; // Prevent call if iti is not ready

    const res = await auth.getProtectedScript({ targeted_element: "#cta_button" }, suppressLoading);

    if (res.data?.messageCode === "00") {
      // Avoid duplicate injection
      const oldScript = document.getElementById("evina-dcb-protect");
      if (oldScript) {
        oldScript.remove();
      }

      var script = document.createElement("script");
      script.id = "evina-dcb-protect";
      script.type = "text/javascript";
      script.text = res.data.success.dcbprotect || res.data.dcbprotect;
      document.head.appendChild(script);

      var ev = new Event("DCBProtectRun");
      document.dispatchEvent(ev);
      
      scriptLoaded.value = true;
    }

    const tid = res.data?.success?.transaction_identify || res.data?.transaction_identify;
    if (tid) {
      console.log("Response:", tid);
      serverTid.value = tid;
      localStorage.setItem("transaction_identify", tid);
    }
  } catch (err) {
    console.error("Failed to fetch protected script:", err);
  }
};

const { t, locale } = useI18n();
const errors = computed(() => auth.errors);

// 🚀 التحقق من صحة الرقم عبر Computed Property (Vue Way)
const isPhoneNumberValid = computed(() => {
  const val = msisdn.value.trim();
  if (!iti.value) return false;

  if (selectedCountry.value === 'LY') {
    // ليبيا: 10 أرقام بالضبط تبدأ بـ 091, 092, 093, 094, 095
    const lyPattern = /^09[12345]\d{7}$/;
    return lyPattern.test(val);
  } else {
    // السعودية: بين 7 و 12 خانة
    const saPattern = /^\d{7,12}$/;
    return saPattern.test(val);
  }
});
const email = ref('')
const sendOTP = async () => {
  auth.clearError();
  localError.value = "";

  try {
    if (!iti.value) return;

    let fullNumber = iti.value.getNumber(window.intlTelInputUtils?.numberFormat.E164).replace("+", "");
   const userEmail = email.value // get the email from the input field 
    // في حالة لم يكتمل تحميل سكريبت المكتبة وكان الرقم فارغاً، نستخدم الفالباك (الرقم الممرر)
    if (!fullNumber && msisdn.value) {
      // إزالة علامة + أو الأصفار إذا وجدت
      fullNumber = msisdn.value.replace(/^\+/, '');
    }
    
    const countryData = iti.value.getSelectedCountryData();
    const countryIso = countryData?.iso2?.toUpperCase();
    
    let tid = "";
    if (countryIso === "SA") {
      // نظام السعودية: توليد يدوي كما جربناه ونجح 100%
      tid = `SA-WEB-${Date.now()}`;
    } else {
      // نظام ليبيا: استخدام المعرف الرسمي القادم من السيرفر (Protected Script)
      tid = serverTid.value || `${countryIso}-WEB-${Date.now()}`; // fallback لو فشل السيرفر
    }

    // البايلود الأساسي يشمل رقم التلفون ومعرف التحقق (الفرود)
    let payload = { 
      msisdn: fullNumber, 
     email: userEmail || null,
      transaction_identify: tid 
    };

    // قراءة الإيميل من الحقل المخفي إذا كان الموبايل قد أضافه عبر WebView Injection
    const injectedEmail = document.getElementById('user-email')?.value;
    if (injectedEmail && injectedEmail.trim() !== '') {
      payload.email = injectedEmail.trim();
      // حفظ الإيميل في localStorage بيش يتبعت مع confirmOTP في صفحة OTP أيضاً
      localStorage.setItem('injected_email', injectedEmail.trim());
    }
    
    // حفظ المعرف في الذاكرة لجميع الدول
    localStorage.setItem("transaction_identify", tid);
    
    // إضافة حقن مزود الخدمة فقط إذا كانت الدولة ليبيا
    if (countryIso === "LY") {
      const localNum = fullNumber.replace(/^218/, ''); // إزالة كود الدولة للتأكد من البادئة
      if (localNum.startsWith('91') || localNum.startsWith('93')) {
        payload.operator = "Al-Madar";
      } else {
        payload.operator = "Libyana";
      }
    }

    const response = await auth.login(payload);
    const flow = resolveFlow(response);

    if (flow.type === "error") {
      localError.value = flow.message || t("errors.connection_error");
    } else {
      localStorage.setItem("msisdn", fullNumber);
      localStorage.setItem("transaction_identify", tid);
      executeFlow(flow, fullNumber);
    }
  } catch (err) {
    localError.value = t("errors.network_error");
  } finally {
    // Safety fallback to ensure button is never stuck
    auth.loading = false;
  }
};

onMounted(async () => {
  // مسح أي أخطاء سابقة عند تحميل المكون
  auth.clearError();

  const inputEl = phoneNumberInput.value

  // فحص الرابط للعودة من STC
  if (route.query.error === 'stc_callback_failed') {
    localError.value = "حالة الاشتراك غير مفعلة أو قيد الانتظار، يرجى المحاولة لاحقاً.";
    // تنظيف الرابط
    router.replace({ query: { ...route.query, error: undefined } });
  }

  // تهيئة intl-tel-input
  iti.value = window.intlTelInput(inputEl, {
    initialCountry: "sa",
    onlyCountries: ["sa", "ly"],
    utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
    separateDialCode: false,
    placeholderNumberType: "MOBILE",
    showSearchInput: true,
    searchPlaceholder: "Search by name or code",
    dropdownContainer: document.body,
    autoPlaceholder: 'off',
    allowDropdown: true,
  })

  if (route.query.msisdn) {
    const passedMsisdn = route.query.msisdn;
    // تعيين الرقم برمجياً في المكتبة لكي تقرأه sendOTP بشكل صحيح
    setTimeout(() => {
      if (iti.value) iti.value.setNumber("+" + passedMsisdn);
      msisdn.value = passedMsisdn;
      
      // مسح الرقم من الرابط للحفاظ على نظافة الـ URL
      router.replace({ query: { ...route.query, msisdn: undefined } });
      
      // تشغيل زر الاشتراك بشكل تلقائي لاختصار الخطوة على المستخدم
      sendOTP();
    }, 100);
    return;
  }

  // 🚀 مراقب تغيير الدولة لتحديث الـ UI فوراً وبدء عملية الحماية لليبيا
  inputEl.addEventListener("countrychange", () => {
    const countryData = iti.value.getSelectedCountryData();
    selectedCountry.value = countryData.iso2.toUpperCase();
    
    // مسح الرقم القديم لتجنب تعارض الفاليديشن
    msisdn.value = ''; 
    inputEl.value = '';
    localError.value = '';

    // إعادة التركيز على الحقل ليكون جاهزاً للكتابة فوراً بعد تغيير العلم
    setTimeout(() => {
      inputEl.focus();
    }, 150);

    // جلب Protected Script بشكل غير متزامن لليبيا (بدون await لمنع تجميد الحقل)
    if (selectedCountry.value === 'LY') {
      fetchProtectedScript(true);
    }
  });

  // تشغيل السكريبت فورياً إذا كانت ليبيا هي المختارة عند التحميل
  if (iti.value.getSelectedCountryData().iso2.toUpperCase() === 'LY') {
    await fetchProtectedScript(true);
  }

  // 🚀 دالة محسنة لفرض مكان القائمة تحت الحقل دائماً ومنعها من الصعود للأعلى
  const forceDropdownBelow = () => {
    const container = document.querySelector('.iti--container');
    if (container) {
      const rect = inputEl.getBoundingClientRect();
      
      // إجبار الموضع تحت الحقل مباشرة مع مطابقة العرض لتفادي أي "نطرة"
      container.style.setProperty('top', (rect.bottom + window.scrollY) + "px", "important");
      container.style.setProperty('bottom', "auto", "important");
      container.style.setProperty('left', (rect.left + window.scrollX) + "px", "important");
      container.style.setProperty('right', "auto", "important");
      container.style.setProperty('width', rect.width + "px", "important");
      
      // إضافة إزاحة بسيطة للجمالية
      container.style.setProperty('transform', "translateY(5px)", "important");
    }
  };

  const handleOpenDropdown = () => { // تعريف الدالة
    forceDropdownBelow();
    // تنفيذ متكرر لضمان كسر منطق المكتبة الداخلي الذي قد يحاول قلبها بعد الفتح
    setTimeout(forceDropdownBelow, 0);
    setTimeout(forceDropdownBelow, 100);
    setTimeout(forceDropdownBelow, 200);
  };

  inputEl.addEventListener("open:countrydropdown", handleOpenDropdown); // استخدام الدالة

  // 🚀 تم إزالة handleKeyDown لأنها تسبب مشكلة تجميد الكيبورد في الجوالات (Android 229 keyCode)التي ترسل Unidentified
  // وتعتمد المنظومة الآن على handleInput فقط لتنظيف الأرقام وهي الطريقة الأفضل والأكثر توافقاً.

  const handleInput = (e) => {
    const rawValue = e.target.value;
    const cleanValue = rawValue.replace(/\D/g, '');
    
    if (rawValue !== cleanValue) {
      e.target.value = cleanValue;
    }
    msisdn.value = cleanValue;
    auth.clearError();

    // التحقق التدريجي لليبيا
    if (selectedCountry.value === 'LY') {
      if (cleanValue.length === 0) {
        localError.value = '';
      } else if (cleanValue.length >= 2 && !cleanValue.startsWith('09')) {
        localError.value = t('errors.ly_invalid_start');
      } else if (cleanValue.length >= 3 && !/^09[12345]/.test(cleanValue)) {
        localError.value = t('errors.ly_invalid_format');
      } else if (cleanValue.length > 0 && cleanValue.length < 10 && /^09[12345]/.test(cleanValue)) {
        localError.value = '';
      } else if (cleanValue.length > 10) {
        localError.value = t('errors.number_extra');
      } else {
        localError.value = '';
      }
    } else {
      localError.value = '';
    }
  };
  inputEl.addEventListener('input', handleInput); // استخدام الدالة

  deviceType.value = route.query.device_type || 'web'
  isDarkMode.value = route.query.is_dark === 'true';

  // استقبال اللغة من الرابط وتحديث التطبيق
  if (route.query.lang) {
    locale.value = route.query.lang;
  }
  
  // الحل الجذري لمشكلة الكيبورد: جعل الحاوية الرئيسية تتقلص لتناسب المساحة المتاحة
  // 🚀 قفل الـ scroll: يمنع iOS من رفع الخلفية عند الضغط على أي حقل
  const lockScroll = () => {
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  };
  window.addEventListener('scroll', lockScroll, { passive: true });

  // إضافة lockScroll على focus الحقل مباشرة بتأخيرات متعددة
  if (phoneNumberInput.value) {
    phoneNumberInput.value.addEventListener('focus', () => {
      setTimeout(() => window.scrollTo(0, 0), 0);
      setTimeout(() => window.scrollTo(0, 0), 100);
      setTimeout(() => window.scrollTo(0, 0), 300);
    });
  }

  // حفظ المرجع لإزالته عند unmount
  window._loginLockScroll = lockScroll;

  if (window.visualViewport) {
    adjustFooter = () => {
      const vvh = window.visualViewport.height;
      const appScreen = document.querySelector('.app-screen');

      // 🚀 إصلاح: منع الخلفية من الارتفاع — نعيد الـ scroll للصفر دائماً
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      if (appScreen) {
        // تحديث ارتفاع الحاوية ليكون بالضبط مساحة الرؤية المتاحة
        appScreen.style.height = `${vvh}px`;
        
        // اكتشاف حالة الكيبورد بناءً على الفرق بين الطول الكلي والمتاح
        const isCurrentlyOpen = (window.innerHeight - vvh) > 100;
        isKeyboardOpen.value = isCurrentlyOpen;

        if (isCurrentlyOpen) {
          // 🚀 تم إيقاف الـ scrollIntoView لمنع القفزة المفاجئة للخلفية
          // المتصفح سيحاول ضبط الرؤية تلقائياً داخل الحاوية المقلصة
        }
      }
    };
    window.visualViewport.addEventListener('resize', adjustFooter);
    window.visualViewport.addEventListener('scroll', adjustFooter);
    adjustFooter();
  }
})

onBeforeUnmount(() => {
  if (iti.value) {
    iti.value.destroy()
  }
  // إزالة جميع الـ Event Listeners المضافة يدوياً
  const inputEl = phoneNumberInput.value;
  if (inputEl) {
    inputEl.removeEventListener("countrychange", handleCountryChange);
    inputEl.removeEventListener("open:countrydropdown", handleOpenDropdown);
    inputEl.removeEventListener('input', handleInput);
  }
  if (window.visualViewport && adjustFooter) {
    window.visualViewport.removeEventListener('resize', adjustFooter);
    window.visualViewport.removeEventListener('scroll', adjustFooter);
  }
  // إزالة scroll lock
  if (window._loginLockScroll) {
    window.removeEventListener('scroll', window._loginLockScroll);
    delete window._loginLockScroll;
  }
});

// تعريف الدوال خارج onMounted ليمكن إزالتها في onBeforeUnmount
const handleCountryChange = () => { /* تم نقلها لـ onMounted */ };
const handleOpenDropdown = () => { /* تم نقلها لـ onMounted */ };
const handleInput = () => { /* تم نقلها لـ onMounted */ };
</script>

<style scoped>
/* استيراد خط Tajawal و Urbanist */
@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@200;900&family=Urbanist:wght@400;600;700&display=swap');

/* Dark Theme Styles */
.body-wrapper.dark-theme {
  background-color: #121212;
}
.dark-theme .content-wrapper {
  background-color: #1E1E1E;
}
.dark-theme .main-content h1,
.dark-theme .main-content h2,
.dark-theme .main-content p {
  color: #ffffff;
}
.dark-theme .text-secondary {
  color: #a0a0a0 !important;
}
.dark-theme #phone-number {
  background-color: #333 !important; /* ضمان بقاء اللون الداكن */
  color: #ffffff !important;
  border: 2px solid transparent !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}
.dark-theme #phone-number::placeholder {
  color: rgba(255, 255, 255, 0.45) !important;
}
.dark-theme #phone-number:focus {
  outline: none !important;
  border: 2px solid #FF5A36 !important; /* البوردر البرتقالي في الدارك مود */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  background-color: #333 !important; /* العودة للون الداكن بدلاً من الأبيض */
}
.dark-theme .app-footer {
  background-color: #1E1E1E !important;
}
.dark-theme .invalid-feedback {
  color: #ff8a80 !important;
}
.dark-theme .app-screen {
  background-color: #ff5a36; /* Keep the orange background in dark mode */
}

/* تحسين ألوان مكتبة رقم الهاتف في الوضع الداكن */
.dark-theme :deep(.iti__country-list) {
  background-color: #1e1e1e;
  color: #fff;
  border-color: #333;
}
.dark-theme :deep(.iti__search-input) {
  background-color: #333;
  color: #fff;
  border-color: #444;
}
.dark-theme :deep(.iti__country:hover), 
.dark-theme :deep(.iti__country.iti__highlight) {
  background-color: #333;
}

/* RTL Styles */
[dir="rtl"] .main-content, [dir="rtl"] .app-header {
  text-align: right;
}

[dir="rtl"] .back-button {
  transform: scaleX(-1);
}

[dir="rtl"] #phone-number {
  text-align: right;
}

/* --- RTL adjustments for intl-tel-input dropdown --- */
[dir="rtl"] :deep(.iti--container) {
  text-align: right; /* محاذاة النص داخل القائمة لليمين فقط */
}

/* إضافة قص للنص الطويل داخل القائمة */
:deep(.iti__country-name) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px; /* يمكن تعديل هذه القيمة حسب الحاجة */
}


/* General Styles for Mobile Screen Simulation */
.body-wrapper {
  background-color: #ff5a36; /* خليه برتقالي نفس الثيم عشان لو صار أي فراغ يغطيه */
  font-family: 'Tajawal', sans-serif;
  min-height: 100vh;
}

.app-screen {
  max-width: 400px;
  height: 100dvh; /* 🚀 استخدام الارتفاع الديناميكي لحل مشاكل المتصفحات الحديثة */
  background-color: #ff5a36;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  margin: auto;
  display: flex;
  flex-direction: column;
  position: relative; /* مهم */
  overflow: hidden; /* عشان نمنع السكرول الخارجي ونخليه داخل الـ main-content بس */
  padding: 0; /* إزالة الحشوة من الشاشة الرئيسية */
  background-image: url('@/assets/pic/vweb.png'); /* 🚀 نقل: صورة الخلفية إلى هنا */
  background-size: 86% auto; /* 🚀 تعديل: تصغير حجم الصورة قليلاً */
  background-position: center top; /* جعل الصورة تبدأ من الأعلى */
  background-repeat: no-repeat;
  background-attachment: local; /* يخلي الخلفية ثابتة مع الحاوية */
}

/* تنسيق العنصر الأبيض الجديد */
.content-wrapper {
  background-color: #ffffff;
  border-top-left-radius: 40px; /* زيادة استدارة الحواف */
  border-top-right-radius: 40px; /* زيادة استدارة الحواف */
  flex-grow: 4; /* 🚀 رفع إضافي للأعلى لضمان ظهور الزر */
  /* تم إزالة الهامش العلوي والاعتماد على flexbox للتوسيط */
  display: flex;
  flex-direction: column;
  padding: 40px 20px 20px 20px;
  overflow: visible; /* نخلي shadow وcorners الحقل تظهر كاملة */
}

/* تعديل تنسيق الهيدر ليحتوي على اللوقو */
.app-header {
  flex-shrink: 0; /* منع الهيدر من التقلص */
  display: flex;
  justify-content: center;
  align-items: flex-end; /* 🚀 تعديل: إنزال اللوقو للأسفل */
  flex-grow: 1; 
  padding-bottom: 0; /* إزالة المسافة السفلية للسماح بالتحكم عبر الهامش */
}

.login-logo {
  width: 170px; 
  height: auto;
  position: relative; /* ضروري ليعمل z-index */
  z-index: 2; /* لضمان ظهور اللوقو فوق المنطقة البيضاء */
  margin-bottom: -10px; /* 🚀 تعديل: رفع اللوقو للأعلى قليلاً */
}

.back-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background-color: transparent;
  border-radius: 50%;
  color: #333;
  text-decoration: none;
  transition: color 0.3s, background-color 0.3s;
}

.back-button:hover {
  color: #ff5a36;
  background-color: #f5f5f5; /* إضافة خلفية ناعمة عند المرور */
}

.back-button svg {
  width: 24px; /* تكبير الأيقونة قليلاً لتناسب التصميم الجديد */
  height: 24px;
}

/* --- Styling for intl-tel-input to match your design --- */
/* We use :deep() to style child components from a scoped style block */
:deep(.iti) { 
  width: 100%; /* Make the input full width */
  display: block; /* ضمان أن الحاوية تأخذ المساحة العرضية كاملة */
  direction: ltr; /* 🚀 إضافة: إجبار الحقل على اتجاه LTR دائماً */
}
:deep(.iti__flag-container) {
    /* Adjust the dropdown trigger appearance */
    user-select: none;
}
:deep(.iti__selected-flag) {
    /* Center the larger flag vertically */
    display: flex;
    align-items: center;
    height: 100%;
    padding-left: 18px; /* 🚀 زيادة المسافة بين العلم وحافة الإدخال */
}

#phone-number {
    width: 100%;
    display: block;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
    border: 2px solid transparent; /* بوردر شفاف بالحجم الثابت يتحول للبرتقالي عند الضغط */
    border-radius: 30px; /* نفس استدارة حقل الـ OTP */
    padding: 12px 20px 12px 70px;
    font-size: 13px;
    font-family: 'Urbanist', 'Tajawal', sans-serif;
    color: #333;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #f5f5f5;
    height: auto;
    transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;
}

/* 🚀 عند الضغط: بوردر برتقالي نفس حقل OTP */
#phone-number:focus {
    outline: none !important;
    border: 2px solid #FF5A36 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
    background-color: #fff;
}

/* --- Sticky Footer Styling --- */
.app-footer {
  padding: 1rem 0;
  background-color: #fff;
  /* The following properties make the footer sticky */
  position: sticky;
  bottom: 0;
  /* On larger screens, we don't want it fixed */
  justify-content: center; /* توسيط المحتوى (الزر) */
  box-shadow: none !important;
}

/* 🚀 إضافة: حركة دخول للعنوان */
@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* تنسيق عنوان الترحيب */
.welcome-title {
  font-size: 1.8rem; /* تكبير العنوان ليكون أكثر وضوحاً */
  line-height: 1.4; /* تحسين ارتفاع السطر لإظهار المدة بشكل صحيح */
  animation: slideInUp 0.6s ease-out forwards; /* تطبيق حركة الدخول */
}

.main-content p {
  font-size: 0.8rem; /* تصغير نص الوصف أكثر */
}

.main-content {
  flex-grow: 1; /* Allow main content to take up available space */
  overflow-y: auto; /* Allow content to scroll if it overflows */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.main-content::-webkit-scrollbar {
  display: none;
}

/* --- General CTA Button Styling --- */
.btn-custom-primary {
  background-color: #FD451C !important;
  color: white !important;
  border: none;
  border-radius: 30px; /* زيادة استدارة الحواف */
  font-weight: 700;
  padding: 15px 0;
  min-height: 54px; /* 🚀 إضافة: ضمان الحد الأدنى للارتفاع لمنع الانضغاط */
  width: 94%; /* 🚀 تعديل: التحكم في عرض الزر لترك فراغ جانبي */
  margin: 0 auto; /* توسيط الزر */
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0; /* منع الزر من الانكماش */
}

/* 🚀 استهداف سكريبت الحماية الخارجي لفرض التنسيق */
:deep(.cptpl_subscribe), 
#cta_button {
  min-height: 54px !important;
  height: 54px !important;
  background-color: #FD451C !important;
  border-radius: 30px !important;
  overflow: hidden;
}

.btn-custom-primary:disabled {
  background-color: rgba(253, 69, 28, 0.5) !important;
  cursor: not-allowed;
}

/* --- Media Queries for smaller screens --- */
@media (max-width: 768px) {
  .body-wrapper {
  }
  .app-screen {
    width: 100%; /* جعل عرض الصفحة يملأ الشاشة بالكامل */
    max-width: 100%; /* إزالة أي قيود على العرض الأقصى */
    height: 100vh;
    height: 100dvh;
    box-shadow: none;
    border-radius: 0;
    padding: 0; /* إزالة الحشوة في شاشات الجوال أيضاً */
    overflow: hidden; /* منع الخلفية من الارتفاع مع التمرير */
  }
  /* 🚀 إضافة: جعل الفوتر ثابتًا في أسفل الشاشة على الجوال */
    .app-footer {
      position: relative; /* تغيير من fixed إلى relative ليعتمد على الـ flexbox */
      width: 100%;
      background-color: white !important;
      padding: 15px 0;
      min-height: 80px;
      z-index: 100;
      /* إزالة الـ fixed positioning والاعتماد على تقلص الـ app-screen */
      box-shadow: none !important;
    }
    .dark-theme .app-footer {
      background-color: #1E1E1E !important;
      box-shadow: none !important;
    }
  .main-content {
    padding-bottom: 100px !important; /* مساحة لضمان عدم اختفاء الشروط خلف الزر الثابت */
  }
}

/* --- Very Small Screens: Shrink content to fit without scrolling --- */
@media (max-width: 376px), (max-height: 740px) {
  .login-logo {
    width: 120px; /* تصغير اللوقو */
  }
  .welcome-title {
    font-size: 1.4rem; /* تصغير العنوان */
  }
  .mb-4 {
    margin-bottom: 0.8rem !important; /* تقليل المسافات */
  }
  .terms-text, .service-price-note {
    font-size: 0.7rem; /* تصغير النص */
  }
  .terms-title {
    font-size: 0.9rem;
  }
  #phone-number {
    padding-left: 50px !important; /* تقليص الحشوة للعلم في الشاشات الضيقة جداً لترك مساحة للنص */
    padding-right: 15px !important;
  }
}

/* input غير صحيح */
input.is-invalid,
textarea.is-invalid,
select.is-invalid {
  border: 1px solid !important;
  border-color: #dc3545 !important; 
  padding-right: 2.25rem;
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

/* رسالة الخطأ */
.invalid-feedback {
  display: block; /* أهم حاجة تظهر الرسالة */
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545 !important;
}

/* نص السعر تحت الحقل */
.service-price-note {
  color: #a0a0a0; /* رمادي فاتح كما في الصورة */
  font-size: 0.8rem;
  margin-top: 8px;
}

/* حاوية الشروط */
.terms-container {
  margin-top: 50px;
  padding: 0 10px;
}

/* العناوين الفرعية (الشروط / الإلغاء) */
.terms-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #000000;
  margin-bottom: 8px;
}

.terms-title--normal {
  font-weight: normal;
  font-size: 0.9rem;
}

/* نصوص الشروط الصغيرة */
.terms-text {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #000000;
  margin-bottom: 15px;
}

/* تأكدي من ضبط اتجاه النص لليمين */
[dir="rtl"] .terms-container {
  text-align: right;
}

/* 🚀 لما يكون الكيبورد مفتوح، نصغر كل شي فوق */
.keyboard-open .app-header {
  /* 🚀 توقفنا عن تصغير الهيدر لمنع قفزة الخلفية البيضاء للأعلى */
  min-height: 120px !important; 
}
.keyboard-open .login-logo {
  width: 80px !important; /* تصغير اللوجو جداً */
  margin-bottom: 0 !important;
}
.keyboard-open .content-wrapper {
  /* 🚀 الحفاظ على نفس الحشوة لمنع الارتفاع المفاجئ */
  padding-top: 40px !important; 
}
.keyboard-open .welcome-title {
  font-size: 1.1rem !important; /* تصغير "Welcome" */
  margin-bottom: 5px !important;
}
.keyboard-open .text-secondary {
  font-size: 0.7rem !important; /* تصغير الوصف */
  margin-bottom: 10px !important;
}
.keyboard-open #phone-number {
  margin-bottom: 10px !important; /* تقليل المسافة ليكون السعر قريباً من الحقل */
}

/* إخفاء الشروط بمجرد فتح الكيبورد */
.keyboard-open .terms-container {
  display: none !important;
}

/* إظهار نوت السعر وتأمين مسافة كافية لكي لا يغطيه الزر */
.keyboard-open .service-price-note {
  display: block !important;
  margin-bottom: 20px !important; /* 🚀 تقليل المسافة لترك مساحة للزر */
  font-size: 0.75rem !important;
}

/* 🚀 إلغاء السكرول تماماً ومنع المساحات البيضاء عند فتح الكيبورد */
.keyboard-open.app-screen {
  overflow: hidden !important;
}
.keyboard-open .main-content {
  padding-bottom: 20px !important;
  overflow-y: auto !important; /* 🚀 السماح بالتمرير داخل المنطقة البيضاء حتى والكيبورد مفتوح */
}
</style>

<style>
/* استايلات عامة للقائمة لضمان ظهورها تحت الحقل دائماً وبشكل صحيح */
.iti--container {
  z-index: 999999 !important;
  position: absolute !important;
}

/* تنسيق القائمة نفسها لتأخذ العرض الكامل للحاوية */
.iti__country-list {
  top: 0 !important;
  bottom: auto !important;
  position: static !important;
  width: 100% !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15) !important;
  border: none !important;
  max-height: 200px !important;
}
</style>